/**
 * SPA entry point.
 * Sets up React Router with all page routes, wraps the app
 * in context providers, and mounts to the DOM.
 *
 * @module index
 */

import React                from 'react';
import ReactDOM             from 'react-dom/client';
import { createBrowserRouter,
         RouterProvider,
         redirect }         from 'react-router-dom';
import { AuthProvider }     from './dataProviders/AuthProvider';
import { I18nProvider }     from './dataProviders/I18nProvider';
import { ThemeProvider }    from './dataProviders/ThemeProvider';
import apiClient            from './procedural/apiClient';
import config               from './config';
import './index.css';

import Home           from './pages/home';
import Signup         from './pages/signup';
import Login          from './pages/login';
import LoginWithCode  from './pages/loginWithCode';
import ChangePassword from './pages/changePassword';
import Settings       from './pages/settings';
import Dashboard      from './pages/dashboard';
import NotLoggedIn    from './pages/notLoggedIn';
import InternalError  from './pages/internalError';
import HelpIndex      from './pages/help/HelpIndex';
import HelpTopic      from './pages/help/HelpTopic';
import BlogPost       from './pages/blogPost';

/**
 * Route loader that redirects unauthenticated users.
 * Checks the session by calling the BFF settings endpoint.
 *
 * @returns {Promise<null|Response>}
 */
async function requireAuth() {
  try {
    await apiClient.get('/api/settings');
    return null;
  } catch {
    return redirect(config.pagePaths.notLoggedIn);
  }
}

const router = createBrowserRouter([
  { path: config.pagePaths.home,           element: <Home /> },
  { path: config.pagePaths.signup,         element: <Signup /> },
  { path: config.pagePaths.login,          element: <Login /> },
  { path: config.pagePaths.loginWithCode,  element: <LoginWithCode /> },
  { path: config.pagePaths.changePassword, element: <ChangePassword /> },
  { path: config.pagePaths.blog,            element: <BlogPost /> },
  { path: config.pagePaths.blogPost,       element: <BlogPost /> },
  { path: config.pagePaths.notLoggedIn,    element: <NotLoggedIn /> },
  { path: config.pagePaths.internalError,  element: <InternalError /> },
  /* Authenticated routes */
  { path: config.pagePaths.help,           element: <HelpIndex />,  loader: requireAuth },
  { path: config.pagePaths.helpTopic,      element: <HelpTopic />,  loader: requireAuth },
  { path: config.pagePaths.settings,       element: <Settings />,   loader: requireAuth },
  { path: config.pagePaths.dashboard,      element: <Dashboard />,  loader: requireAuth },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  </React.StrictMode>
);
